import SideMenu from "./SideMenu/SideMenu"
import Header from "./Header/Header"
import Footer from "./Footer/Footer"
import { SpeedDial, SpeedDialAction, SpeedDialIcon } from "@mui/material"
import { AccountTree, Contacts, RecentActors } from "@mui/icons-material"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import CustomersConfig from "../../lib/config/customers"
import { FormProvider } from "../../contexts/CreateRecordFormContext"
import { useAuth } from "../../contexts/AuthContext"
import RecordModal from "../RecordModal/RecordModal"
import { API } from "../../lib/api"
import Feedback from "../Feedback/Feedback"
import { writeCachedUserSettings } from "../../lib/userSettingsCache"
import opportunityCreateFields from "../../lib/config/opportunityCreateFields"

const BaseWrapper = ({ content }) => {
    const [customerModal, setCustomerModal] = useState(false)
    const [opportunityModal, setOpportunityModal] = useState(false)
    const [contactModal, setContactModal] = useState(false)
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""}) 
    const [customers, setCustomers] = useState([])
    const [userSettings, setUserSettings] = useState({})
    const [customersLoaded, setCustomersLoaded] = useState(false)
    const { user } = useAuth()
    const navigate = useNavigate()
    const resolvedUserId = user?.userId || user?.id || localStorage.id

    const handleSideBar = () => {
        let side = document.querySelector(".pc-sidebar")
        side.classList.remove("mob-sidebar-active")
    }

    const handleOpenCustomerModal = () => setCustomerModal(true);
    const handleCloseCustomerModal = () => setCustomerModal(false);

    const loadCustomers = async () => {
        if (customersLoaded) {
            return true
        }

        const response = await API.Customers.FetchAll()
        const tempCustomers = response.map(cust => ({
            value: cust._id,
            label: `${cust.organizationName} - ${cust.city}`
        }))

        setCustomers(tempCustomers)
        setCustomersLoaded(true)
        return true
    }

    const handleOpenOpportunityModal = async () => {
        const loaded = await loadCustomers()
        if (loaded) {
            setOpportunityModal(true)
        }
    };
    const handleCloseOpportunityModal = () => setOpportunityModal(false);

    const handleOpenContactModal = async () => {
        const loaded = await loadCustomers()
        if (loaded) {
            setContactModal(true)
        }
    };
    const handleCloseContactModal = () => setContactModal(false);

    const handleCreateSubmit = async (data) => {
        const response = await API.Customers.Create(localStorage.id, data)

        if (response.status === 200) {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }
        handleCloseCustomerModal()
    };

    const handleCreateOppSubmit = async (data) => {
        let primaryContact = data?.primaryContact?.value

        const requestReadyData = {
            owner: localStorage.id,
            customer: data?.customer.value,
            name: data.name,
            createdBy: localStorage.email,
            lastUpdatedBy: localStorage.email,
            lastUpdatedDate: Date.now(),
            salesStage: "01 - Lead",
            ...(primaryContact && { primaryContact }),
        }
        const response = await API.Opportunities.Create(requestReadyData)

        if (response.status === 200) {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
            const newOpportunityId = response?.data?.opportunity?._id || response?.data?._id
            handleCloseOpportunityModal()
            if (newOpportunityId) {
                navigate(`/opportunities/${newOpportunityId}`)
            }
            return
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }
        handleCloseOpportunityModal()
    }

    const handleCreateContactSubmit = async (data) => {
        const { customer, ...contactData } = data

        const response = await API.Contacts.Create(customer.value, contactData)

        if (response.status === 200) {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }

        handleCloseContactModal()
    }

    const createContactFields = [
        { name: "customer", label: "Customer", type: "autocomplete", options: customers},
        { name: 'firstName', label: "First Name", type: 'text', required: true },
        { name: "lastName", label: "Last Name", type: 'text', required: true },
        { name: "primaryEmail", label: "Email", type: "email" },
        { name: "phoneNumber", label: "Phone" },
        { name: "contactJobTitle", label: "Job Title" },
    ]

    const createOppFields = [
        { name: "customer", label: "Customer", type: "autocomplete", options: customers},
        ...opportunityCreateFields,
    ]

    useEffect(() => {
        if (!resolvedUserId) {
            return
        }

        let ignore = false

        async function fetchSettings() {
            try {
                const response = await API.Settings.FetchByUser(resolvedUserId)
                writeCachedUserSettings(resolvedUserId, response)

                if (!ignore) {
                    setUserSettings(response || {})
                }
            } catch {
                if (!ignore) {
                    setUserSettings({})
                }
            }
        }

        fetchSettings()

        return () => {
            ignore = true
        }
    },[resolvedUserId])

    return (
        <>
            <Feedback props={feedback} setFeedback={setFeedback} />
            {customerModal ? (
                <FormProvider>
                    <RecordModal open={customerModal} handleClose={handleCloseCustomerModal} fields={CustomersConfig.NewCustomerFields} title="Create New Customer" onSubmit={handleCreateSubmit} />
                </FormProvider>
            ) : null}
            {opportunityModal ? (
                <FormProvider>
                    <RecordModal open={opportunityModal} handleClose={handleCloseOpportunityModal} fields={createOppFields} title="Create New Opportunity" onSubmit={handleCreateOppSubmit} />
                </FormProvider>
            ) : null}
            {contactModal ? (
                <FormProvider>
                    <RecordModal open={contactModal} handleClose={handleCloseContactModal} fields={createContactFields} title="Create New Contact" onSubmit={handleCreateContactSubmit} />
                </FormProvider>
            ) : null}
            <div className="loader-bg">
                <div className="loader-track">
                    <div className="loader-fill"></div>
                </div>
            </div>
            <SideMenu/>
            <Header/>
                <div className="pc-container" onClick={handleSideBar}>
                    <div className="pc-content">
                        {content}
                    </div>
                </div> 
            <Footer/>
            {
                userSettings?.speedDial ?
                <SpeedDial
                    ariaLabel="SpeedDial"
                    sx={{ position: 'fixed', bottom: 16, right: 16 }}
                    className="hive-speed-dial"
                    icon={<SpeedDialIcon />}
                >
                    <SpeedDialAction
                        icon={<AccountTree />}
                        tooltipTitle="Create New Opportunity"
                        onClick={handleOpenOpportunityModal}
                        className="hive-speed-dial-action hive-speed-dial-action--opportunity"
                        FabProps={{ className: "hive-speed-dial-fab hive-speed-dial-fab--opportunity" }}
                    />
                    <SpeedDialAction
                        icon={<RecentActors />}
                        tooltipTitle="Create New Customer"
                        onClick={handleOpenCustomerModal}
                        className="hive-speed-dial-action hive-speed-dial-action--customer"
                        FabProps={{ className: "hive-speed-dial-fab hive-speed-dial-fab--customer" }}
                    />
                    <SpeedDialAction
                        icon={<Contacts />}
                        tooltipTitle="Create New Contact"
                        onClick={handleOpenContactModal}
                        className="hive-speed-dial-action hive-speed-dial-action--contact"
                        FabProps={{ className: "hive-speed-dial-fab hive-speed-dial-fab--contact" }}
                    />
                </SpeedDial> 
                :
                null
            }
        </>
    )
}

export default BaseWrapper
